import { ChangeEvent, MutableRefObject, useRef, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import styled from 'styled-components';
import { Feature } from 'geojson';
import mapboxgl from 'mapbox-gl';
import { showError } from '../../../utils/toast';
import parsedKmlToFeatures from '../../../utils/parsedKmlToFeatures';
import ReplaceParcelsModal from '../modals/ReplaceParcelsModal';
import ImportMessagesModal from '../modals/ImportMessagesModal';
import parsedJsonToFeatures from '../../../utils/parsedJsonToFeatures';

const SplitButtonContainer = styled(ButtonGroup)`
  position: relative;
  display: inline-flex;
  align-items: center;

  > button:first-child {
    border-radius: 18px 0 0 18px;
    padding-left: 14px;
    padding-right: 4px;
    &:active,
    &:focus,
    &:hover {
      background: #f3f6fa;
      border-color: #b3c2d1;
      box-shadow: 0 4px 12px 0 rgba(60, 100, 180, 0.1);
    }
    &:active {
      background: #e9eef5;
      border-color: #8fa4bf;
      box-shadow: 0 2px 6px 0 rgba(60, 100, 180, 0.08);
    }
  }

  .dropdown-toggle-split {
    border-radius: 0 18px 18px 0;
    padding-left: 4px;
    padding-right: 14px;
    &:active,
    &:focus,
    &:hover {
      background: #f3f6fa;
      border-color: #b3c2d1;
      box-shadow: 0 4px 12px 0 rgba(60, 100, 180, 0.1);
    }
    &:active {
      background: #e9eef5;
      border-color: #8fa4bf;
      box-shadow: 0 2px 6px 0 rgba(60, 100, 180, 0.08);
    }
  }

  .dropdown-menu {
    font-size: 0.85rem;
    min-width: fit-content;
  }

  .dropdown-item {
    padding: 0.4rem 1rem;
  }
`;

const ImportParcelsButton = ({
  mapRef,
  parcels,
  setParcels,
}: {
  mapRef: MutableRefObject<mapboxgl.Map | null>;
  parcels: Feature[];
  setParcels: (features: Feature[]) => void;
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pendingFeatures, setPendingFeatures] = useState<Feature[]>([]);
  const [messages, setMessages] = useState<{
    info?: string;
    warnings?: string[];
    errors?: string[];
  }>({});
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const kmlFileInputRef = useRef<HTMLInputElement | null>(null);
  const geoJsonFileInputRef = useRef<HTMLInputElement | null>(null);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const handleKmlFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const parsedKml = new DOMParser().parseFromString(
          event.target?.result as string,
          'text/xml',
        );

        // Check for XML parsing errors
        if (!parsedKml || parsedKml.querySelector('parsererror')) {
          showError('The imported KML file is not valid XML.');
          return;
        }

        const {
          features,
          info,
          warnings,
          errors,
        } = parsedKmlToFeatures(parsedKml, ['Polygon', 'MultiPolygon']);

        if (features.length) {
          setPendingFeatures(features);
        }

        if (warnings.length || errors.length) {
          setMessages({
            info,
            warnings,
            errors,
          });
          setIsMessageModalOpen(true);
        }
      } catch (err) {
        showError('An error occurred while parsing the imported KML.');
      }
    };

    reader.readAsText(file);
    e.target.value = ''; // Reset the input value to allow re-uploading the same file
  };

  const handleGeoJsonFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        let geojson;
        try {
          geojson = JSON.parse(text);
        } catch (err) {
          showError('The imported file is not valid JSON.');
          return;
        }

        const { features, info, warnings, errors } = parsedJsonToFeatures(
          geojson,
        );

        if (features.length) {
          setPendingFeatures(features);
        }

        if (warnings.length || errors.length) {
          setMessages({
            info,
            warnings,
            errors,
          });
          setIsMessageModalOpen(true);
        }
      } catch (err) {
        setMessages({
          errors: ['An error occurred while parsing the imported GeoJSON.'],
        });
        setIsMessageModalOpen(true);
      }
    };

    reader.readAsText(file);
    e.target.value = ''; // Reset the input value to allow re-uploading the same file
  };

  const importKML = () => {
    setDropdownOpen(false);
    kmlFileInputRef.current?.click();
  };

  const importGeoJson = () => {
    setDropdownOpen(false);
    geoJsonFileInputRef.current?.click();
  };

  return (
    <>
      <SplitButtonContainer>
        <Button onClick={importKML} color="light">
          Import parcels
        </Button>
        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} direction="up">
          <DropdownToggle
            split
            color="light"
            className="dropdown-toggle-split"
          />
          <DropdownMenu className="dropdown-menu" right>
            <DropdownItem onClick={importKML}>Import KML</DropdownItem>
            <DropdownItem onClick={importGeoJson}>Import GeoJSON</DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </SplitButtonContainer>
      <input
        type="file"
        ref={kmlFileInputRef}
        className="d-none"
        accept=".kml"
        onChange={handleKmlFile}
      />
      <input
        type="file"
        ref={geoJsonFileInputRef}
        className="d-none"
        accept=".json,.geojson,application/geo+json,application/json"
        onChange={handleGeoJsonFile}
      />
      <ReplaceParcelsModal
        pendingFeatures={pendingFeatures}
        setPendingFeatures={setPendingFeatures}
        parcels={parcels}
        setParcels={setParcels}
        mapRef={mapRef}
      />
      <ImportMessagesModal
        messages={messages}
        setMessages={setMessages}
        isOpen={isMessageModalOpen}
        setIsOpen={setIsMessageModalOpen}
      />
    </>
  );
};

export default ImportParcelsButton;
